import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Sparkles, ChevronRight, Loader2, CheckCircle2 } from 'lucide-react';

/**
 * DashboardPerceptionBanner
 * — Non-blocking banner shown on the subscriber dashboard until the
 *   ReGenflō Shift onboarding is completed.
 * — When complete, shows a compact "Biological Participant" badge instead.
 * — Honours `should_show_banner` from backend (handles "remind me later").
 */
export default function DashboardPerceptionBanner() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/perception-onboarding/status`)
      .then(r => { if (mounted) setStatus(r.data); })
      .catch(() => {})
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, []);

  if (loading) return null;
  if (!status) return null;

  // Completed — show small participant badge
  if (status.status === 'completed') {
    return (
      <div
        className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#E1EDE4] border border-[#C9DCC9] text-[#2E6B3B] w-fit mx-auto"
        data-testid="participant-badge"
      >
        <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} />
        <span className="text-[11px] font-['Work_Sans'] uppercase tracking-[0.2em]">Biological Participant</span>
      </div>
    );
  }

  // Suppressed via remind-later
  if (status.should_show_banner === false) return null;

  // F15 — limit reached: show stronger persistent CTA
  if (status.remind_limit_reached && status.status !== 'completed') {
    return (
      <button
        type="button"
        onClick={() => navigate('/perception-shift')}
        className="w-full text-left rounded-2xl border border-[#D9B8A8] bg-gradient-to-r from-[#FBE9DE] to-[#F5D5C0] p-4 sm:p-5 hover:from-[#F5D5C0] hover:to-[#EAC1A6] transition-all duration-300 group"
        data-testid="perception-banner-limit-reached"
      >
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0">
            <Sparkles className="h-4 w-4 text-[#A0512A]" strokeWidth={1.5} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#A0512A] font-['Work_Sans'] mb-0.5">
              Please complete this step
            </p>
            <p className="text-base sm:text-lg font-['Outfit'] text-[#2C2A29] leading-tight">
              Begin the ReGenfl&#333; Shift
            </p>
            <p className="text-xs text-[#5C5955] font-['Work_Sans'] mt-0.5">
              You&rsquo;ve postponed this a few times. Please complete the ReGenfl&#333; Shift before continuing.
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-[#A0512A] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
        </div>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={() => navigate('/perception-shift')}
      className="w-full text-left rounded-2xl border border-[#E7E2D6] bg-gradient-to-r from-[#FAF6EC] to-[#F4ECD9] p-4 sm:p-5 hover:from-[#F4ECD9] hover:to-[#E9DDC2] transition-all duration-300 group"
      data-testid="perception-banner"
    >
      <div className="flex items-center gap-3 sm:gap-4">
        <div className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center flex-shrink-0">
          <Sparkles className="h-4 w-4 text-[#8C6B55]" strokeWidth={1.5} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.25em] text-[#8C6B55] font-['Work_Sans'] mb-0.5">
            A short orientation
          </p>
          <p className="text-base sm:text-lg font-['Outfit'] text-[#2C2A29] leading-tight">
            Begin the ReGenflō Shift
          </p>
          <p className="text-xs text-[#5C5955] font-['Work_Sans'] mt-0.5">
            A brief orientation to help you observe your body through CARE OS.
          </p>
        </div>
        <ChevronRight className="h-4 w-4 text-[#8C6B55] group-hover:translate-x-0.5 transition-transform flex-shrink-0" />
      </div>
    </button>
  );
}
